/**
 * @file outlineBubbleGeometry.ts
 * @brief Numbers, tail path and tone classes for OutlineBubble.
 *
 * @details
 * Split from OutlineBubble.tsx so that file exports components only (React
 * Fast Refresh). Values are the DES-approved mobile ones; see
 * FRONTEND/src/shared/components/VZUIKit/VZOutlineBubble.tsx.
 *
 * @author DES
 * @date 30-09-2026
 */

export const BUBBLE_STROKE = 2;
export const RADIUS = 22;
/** How far the tail's tip drops below the bubble's bottom edge. */
export const TAIL = 18;
/** Radius of the tail's rounded tip. */
export const TIP_R = 9;
/** How far along the bottom edge the swoop starts, from the side line. */
export const TAIL_SPAN = 48;
/** Room either side of the tail's SVG so the dot and the stroke never clip. */
export const SVG_PAD = 6;
export const DOT_R = 5;
export const DOT_GAP = 6;
/** Space the tail and dot need under the bubble. */
export const TAIL_SPACE = TAIL + DOT_GAP + DOT_R * 2 + 2;
/** Air either side of a label in its break. */
export const LABEL_PAD = 8;
/** A break starts this far in from its corner. */
export const LABEL_INSET = 10;
/** Head-room above the outline for the top label, which straddles the line. */
export const LABEL_HALF = 11;
/** A break with no label in it. */
export const PLAIN_GAP = 16;
export const KAPPA = 0.5523;

export const S = BUBBLE_STROKE / 2;
export const TAIL_SVG_W = SVG_PAD * 2 + TAIL_SPAN + BUBBLE_STROKE;
export const TAIL_SVG_H = TAIL_SPACE + BUBBLE_STROKE;
export const TAIL_PATH_START = (() => {
  const L = SVG_PAD + S;
  const B = S;
  const tipY = B + TAIL;
  const arcFromX = L + TIP_R;
  const arcToY = tipY - TIP_R;
  return [
    `M${L + TAIL_SPAN},${B}`,
    `C${L + TAIL_SPAN - (TAIL_SPAN - TIP_R) * 0.5},${B} ${
      L + TIP_R + (TAIL_SPAN - TIP_R) * 0.35
    },${tipY} ${arcFromX},${tipY}`,
    `C${arcFromX + (L - arcFromX) * KAPPA},${tipY} ${L},${
      arcToY + (tipY - arcToY) * KAPPA
    } ${L},${arcToY}`,
    `L${L},0`,
  ].join(" ");
})();
export const TAIL_DOT_Y = S + TAIL + DOT_GAP + DOT_R;
export const TAIL_PATH_END = TAIL_PATH_START.replace(
  /(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/g,
  (_m, x: string, y: string) => `${TAIL_SVG_W - Number(x)},${y}`,
);

/**
 * Speaker tone as a CSS colour: speaker A in brand, everyone else a quiet
 * translucent grey (the mobile values), flipped for dark mode.
 */
export const BUBBLE_TONE_CLASS = {
  a: "[--bubble:hsl(var(--brand))]",
  other: "[--bubble:rgba(0,0,0,0.28)] dark:[--bubble:rgba(255,255,255,0.42)]",
} as const;

/**
 * Degrees (0 = right, clockwise) from the ring's centre to the bubble's dot,
 * for a turn laid out bottom-aligned with `rowGap` between ring and bubble.
 */
export function ringAngleToDot(ringBox: number, rowGap: number): number {
  const dotUp = TAIL_SPACE - TAIL - DOT_GAP - DOT_R + S;
  const dy = ringBox / 2 - dotUp;
  const dx = rowGap + ringBox / 2 + S;
  return (Math.atan2(dy, dx) * 180) / Math.PI;
}
